import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Alert,
  Progress,
  Spinner,
} from "reactstrap";
import axios from "axios";
import cogoToast from "cogo-toast";
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaUpload, FaForward } from "react-icons/fa";
import { getEncryptedAccessToken } from "../../services/Utils";
import { BASE_URL, SUCCESS_RESULT, FAILURE_RESULT } from "../../Constants";

const CVA_SAVE_OFFLINE_URL = `${BASE_URL}/api/mobile/v2/cva/save-offline-entitlement`;
const CVA_UPLOAD_STORAGE_PREFIX = "cva_upload_file:";
const LIST_PAGE_SIZE = 80;
const UI_FLUSH_INTERVAL_MS = 250;
const UI_FLUSH_EVERY_N = 20;

const makeRecordKey = (beneficiaryId, childId) =>
  `${beneficiaryId || ""}|${childId ?? ""}`;

const getCampNameFromDetails = (details) => {
  if (details == null || details === "") return "-";
  if (typeof details === "object") return details?.camp_name || "-";
  try {
    return JSON.parse(details)?.camp_name || "-";
  } catch {
    return "-";
  }
};

const formatEntitlements = (entitlements) => {
  if (entitlements == null || entitlements === "") return "-";
  if (typeof entitlements === "string") return entitlements;
  try {
    return JSON.stringify(entitlements);
  } catch {
    return String(entitlements);
  }
};

const loadUploadedKeySet = (fileName) => {
  if (!fileName) return new Set();
  try {
    const raw = localStorage.getItem(`${CVA_UPLOAD_STORAGE_PREFIX}${fileName}`);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.uploaded_keys)) {
      return new Set(parsed.uploaded_keys);
    }
    if (Array.isArray(parsed?.beneficiary_ids)) {
      return new Set(parsed.beneficiary_ids.map((id) => makeRecordKey(id, "")));
    }
    return new Set();
  } catch {
    return new Set();
  }
};

const saveUploadedRecordKey = (fileName, recordKey) => {
  if (!fileName || !recordKey) return;
  const keys = loadUploadedKeySet(fileName);
  if (keys.has(recordKey)) return;
  keys.add(recordKey);
  localStorage.setItem(
    `${CVA_UPLOAD_STORAGE_PREFIX}${fileName}`,
    JSON.stringify({ uploaded_keys: [...keys] })
  );
};

const buildApiPayload = (record) => ({
  sync_token: record.sync_token,
  project_id: record.project_id || "",
  component_id: record.component_id || "",
  cycle_id: record.cycle_id || "",
  beneficiary_id: record.beneficiary_id || "",
  created_at: record.created_at || "",
  details:
    typeof record.details === "string"
      ? record.details
      : JSON.stringify(record.details || {}),
  location:
    typeof record.location === "string"
      ? record.location
      : JSON.stringify(record.location || {}),
});

const toLightRow = (item, index, uploadedKeySet) => {
  const beneficiaryId = item?.beneficiary_id || "";
  const childId = item?.child_id || "";
  const recordKey = makeRecordKey(beneficiaryId, childId);
  const isAlreadyUploaded = recordKey !== "|" && uploadedKeySet.has(recordKey);

  return {
    index,
    key: item.sync_token || `row-${index}`,
    recordKey,
    name: item?.name || "-",
    cardLabel: item?.card_label || "-",
    camp: getCampNameFromDetails(item?.details),
    createdAt: item?.created_at || "-",
    entitlements: formatEntitlements(item?.entitlements),
    uploadStatus: isAlreadyUploaded ? "skipped" : "pending",
    message: isAlreadyUploaded ? "Already uploaded in a previous session" : "",
  };
};

const computeStats = (rows) => {
  let success = 0;
  let failed = 0;
  let skipped = 0;
  let pending = 0;
  for (let i = 0; i < rows.length; i++) {
    const s = rows[i].uploadStatus;
    if (s === "success") success += 1;
    else if (s === "failed") failed += 1;
    else if (s === "skipped") skipped += 1;
    else if (s === "pending" || s === "uploading") pending += 1;
  }
  return { total: rows.length, success, failed, skipped, pending };
};

const UploadCva = () => {
  const fileInputRef = useRef(null);
  const rawRecordsRef = useRef([]);
  const rowsRef = useRef([]);
  const flushTimerRef = useRef(null);

  const [selectedFileName, setSelectedFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [listPage, setListPage] = useState(0);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const flushRowsToState = useCallback(() => {
    setRows(rowsRef.current.slice());
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      flushRowsToState();
    }, UI_FLUSH_INTERVAL_MS);
  }, [flushRowsToState]);

  const patchRow = useCallback(
    (index, patch, immediate = false) => {
      const current = rowsRef.current[index];
      if (!current) return;
      rowsRef.current[index] = { ...current, ...patch };
      if (immediate) {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        flushRowsToState();
      } else {
        scheduleFlush();
      }
    },
    [flushRowsToState, scheduleFlush]
  );

  const filteredIndices = useMemo(() => {
    const indices = [];
    for (let i = 0; i < rows.length; i++) {
      const s = rows[i].uploadStatus;
      if (statusFilter === "all") indices.push(i);
      else if (statusFilter === "success" && s === "success") indices.push(i);
      else if (statusFilter === "failed" && s === "failed") indices.push(i);
      else if (statusFilter === "skipped" && s === "skipped") indices.push(i);
      else if (statusFilter === "pending" && (s === "pending" || s === "uploading")) indices.push(i);
    }
    return indices;
  }, [rows, statusFilter]);

  const totalListPages = Math.max(1, Math.ceil(filteredIndices.length / LIST_PAGE_SIZE));
  const safeListPage = Math.min(listPage, totalListPages - 1);
  const pageStart = safeListPage * LIST_PAGE_SIZE;
  const visibleIndices = filteredIndices.slice(pageStart, pageStart + LIST_PAGE_SIZE);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/json" && !file.name.toLowerCase().endsWith(".json")) {
      cogoToast.error("Please select a valid JSON file", { hideAfter: 4 });
      return;
    }

    try {
      const content = await file.text();
      const jsonData = JSON.parse(content);
      if (!Array.isArray(jsonData)) {
        throw new Error("JSON file must contain an array of records");
      }

      const uploadedKeySet = loadUploadedKeySet(file.name);
      rawRecordsRef.current = jsonData;
      const nextRows = jsonData.map((item, index) => toLightRow(item, index, uploadedKeySet));
      rowsRef.current = nextRows;

      setSelectedFileName(file.name);
      setRows(nextRows);
      setProgressPct(0);
      setStatusFilter("all");
      setListPage(0);
    } catch (error) {
      cogoToast.error(`Invalid JSON file: ${error.message}`, { hideAfter: 5 });
      rawRecordsRef.current = [];
      rowsRef.current = [];
      setSelectedFileName("");
      setRows([]);
    }
  };

  const uploadRecord = async (original, accessToken) => {
    const payload = buildApiPayload(original);

    try {
      const response = await axios.post(CVA_SAVE_OFFLINE_URL, [payload], {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      const itemResult = response?.data?.data?.[0];
      const itemStatus = Number(itemResult?.status ?? response?.data?.status);
      const message = itemResult?.message || response?.data?.message || "No message";

      if (itemStatus === SUCCESS_RESULT) {
        return { uploadStatus: "success", responseStatus: SUCCESS_RESULT, message };
      }
      return {
        uploadStatus: "failed",
        responseStatus: itemStatus || FAILURE_RESULT,
        message,
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.data?.[0]?.message ||
        error.message ||
        "Request failed";
      return { uploadStatus: "failed", responseStatus: FAILURE_RESULT, message };
    }
  };

  const processFile = async () => {
    if (!selectedFileName || rowsRef.current.length === 0) {
      cogoToast.error("Please select a JSON file with records first", { hideAfter: 4 });
      return;
    }

    const accessToken = getEncryptedAccessToken();
    if (!accessToken) {
      cogoToast.error("You must be logged in to upload", { hideAfter: 4 });
      return;
    }

    setIsProcessing(true);
    setProgressPct(0);
    setStatusFilter("all");
    setListPage(0);

    const uploadedKeySet = loadUploadedKeySet(selectedFileName);
    const total = rowsRef.current.length;
    let processedCount = 0;
    let updatesSinceFlush = 0;

    for (let i = 0; i < total; i++) {
      const row = rowsRef.current[i];
      const recordKey = row.recordKey;

      if (row.uploadStatus === "skipped" || (recordKey !== "|" && uploadedKeySet.has(recordKey))) {
        patchRow(i, {
          uploadStatus: "skipped",
          message: "Already uploaded in a previous session",
        });
        processedCount += 1;
        updatesSinceFlush += 1;
        setProgressPct(Math.round((processedCount / total) * 100));
        if (updatesSinceFlush >= UI_FLUSH_EVERY_N) {
          flushRowsToState();
          updatesSinceFlush = 0;
        }
        continue;
      }

      patchRow(i, { uploadStatus: "uploading", message: "" }, true);

      const original = rawRecordsRef.current[i];
      const result = await uploadRecord(original, accessToken);
      patchRow(i, result);
      updatesSinceFlush += 1;

      if (result.uploadStatus === "success" && recordKey !== "|") {
        saveUploadedRecordKey(selectedFileName, recordKey);
        uploadedKeySet.add(recordKey);
      }

      processedCount += 1;
      setProgressPct(Math.round((processedCount / total) * 100));

      if (updatesSinceFlush >= UI_FLUSH_EVERY_N) {
        flushRowsToState();
        updatesSinceFlush = 0;
      }

      if (i < total - 1) {
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    }

    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    flushRowsToState();
    setIsProcessing(false);
    cogoToast.success("Upload processing completed", { hideAfter: 3 });
  };

  const resetUpload = () => {
    rawRecordsRef.current = [];
    rowsRef.current = [];
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    setSelectedFileName("");
    setRows([]);
    setProgressPct(0);
    setStatusFilter("all");
    setListPage(0);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderStatusIcon = (uploadStatus) => {
    if (uploadStatus === "uploading") {
      return <FaSpinner className="text-primary" style={{ fontSize: "1.1rem" }} spin />;
    }
    if (uploadStatus === "success") {
      return <FaCheckCircle className="text-success" style={{ fontSize: "1.1rem" }} />;
    }
    if (uploadStatus === "failed") {
      return <FaTimesCircle className="text-danger" style={{ fontSize: "1.1rem" }} />;
    }
    if (uploadStatus === "skipped") {
      return <FaForward className="text-warning" style={{ fontSize: "1.1rem" }} />;
    }
    return (
      <span
        style={{
          display: "inline-block",
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: "#cbd5e1",
        }}
      />
    );
  };

  const statCardStyle = (active) => ({
    cursor: "pointer",
    border: active ? "2px solid #0d6efd" : "1px solid #dee2e6",
    borderRadius: 10,
    padding: "14px 12px",
    textAlign: "center",
    backgroundColor: active ? "#f0f7ff" : "#fff",
    transition: "all 0.15s ease",
    userSelect: "none",
  });

  const setFilter = (filter) => {
    setStatusFilter(filter);
    setListPage(0);
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <CardHeader className="bg-primary text-white">
              <h4 className="mb-0 d-flex align-items-center gap-2">
                <FaUpload />
                Upload CVA Offline Entitlements
              </h4>
            </CardHeader>
            <CardBody>
              <Row className="mb-4 align-items-end g-3">
                <Col md={8}>
                  <label className="form-label fw-bold">Select JSON file</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                    accept=".json,application/json"
                    onChange={handleFileSelect}
                    disabled={isProcessing}
                  />
                  <div className="form-text">
                    Upload a JSON array of offline CVA entitlement records. Each record is sent one
                    by one to the server. Previously uploaded beneficiary + child pairs are skipped
                    automatically.
                  </div>
                </Col>
                <Col md={4}>
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={processFile}
                    disabled={
                      !selectedFileName ||
                      rows.length === 0 ||
                      isProcessing ||
                      stats.pending === 0
                    }
                  >
                    {isProcessing ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaUpload className="me-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

              {selectedFileName && (
                <Alert color="info" className="mb-3">
                  <strong>Selected file:</strong> {selectedFileName} ({stats.total} records)
                  {stats.skipped > 0 && (
                    <span className="ms-2">
                      — {stats.skipped} record(s) will be skipped (beneficiary + child already
                      uploaded)
                    </span>
                  )}
                </Alert>
              )}

              {(isProcessing || progressPct > 0) && rows.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-semibold">Progress</span>
                    <span>{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} color="primary" />
                </div>
              )}

              {rows.length > 0 && (
                <Row className="mb-4 g-3">
                  <Col xs={6} sm={4} lg={2}>
                    <div
                      role="button"
                      tabIndex={0}
                      style={statCardStyle(statusFilter === "all")}
                      onClick={() => setFilter("all")}
                      onKeyDown={(e) => e.key === "Enter" && setFilter("all")}
                    >
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#0d6efd" }}>
                        {stats.total}
                      </div>
                      <div className="text-muted small">Total</div>
                    </div>
                  </Col>
                  <Col xs={6} sm={4} lg={2}>
                    <div
                      role="button"
                      tabIndex={0}
                      style={statCardStyle(statusFilter === "success")}
                      onClick={() => setFilter("success")}
                      onKeyDown={(e) => e.key === "Enter" && setFilter("success")}
                    >
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#16a34a" }}>
                        {stats.success}
                      </div>
                      <div className="text-muted small">Success</div>
                    </div>
                  </Col>
                  <Col xs={6} sm={4} lg={2}>
                    <div
                      role="button"
                      tabIndex={0}
                      style={statCardStyle(statusFilter === "failed")}
                      onClick={() => setFilter("failed")}
                      onKeyDown={(e) => e.key === "Enter" && setFilter("failed")}
                    >
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#dc2626" }}>
                        {stats.failed}
                      </div>
                      <div className="text-muted small">Failed</div>
                    </div>
                  </Col>
                  <Col xs={6} sm={4} lg={2}>
                    <div
                      role="button"
                      tabIndex={0}
                      style={statCardStyle(statusFilter === "skipped")}
                      onClick={() => setFilter("skipped")}
                      onKeyDown={(e) => e.key === "Enter" && setFilter("skipped")}
                    >
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#d97706" }}>
                        {stats.skipped}
                      </div>
                      <div className="text-muted small">Skipped</div>
                    </div>
                  </Col>
                  <Col xs={6} sm={4} lg={2}>
                    <div
                      role="button"
                      tabIndex={0}
                      style={statCardStyle(statusFilter === "pending")}
                      onClick={() => setFilter("pending")}
                      onKeyDown={(e) => e.key === "Enter" && setFilter("pending")}
                    >
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#64748b" }}>
                        {stats.pending}
                      </div>
                      <div className="text-muted small">Pending</div>
                    </div>
                  </Col>
                </Row>
              )}

              {visibleIndices.length > 0 && (
                <>
                  <div
                    style={{
                      maxHeight: "520px",
                      overflowY: "auto",
                      border: "1px solid #e9ecef",
                      borderRadius: 10,
                    }}
                  >
                    {visibleIndices.map((rowIndex) => {
                      const entry = rows[rowIndex];
                      return (
                        <div
                          key={entry.key}
                          style={{
                            display: "flex",
                            gap: 12,
                            padding: "12px 14px",
                            borderBottom: "1px solid #e9ecef",
                            backgroundColor:
                              entry.uploadStatus === "success"
                                ? "#f0fdf4"
                                : entry.uploadStatus === "failed"
                                ? "#fef2f2"
                                : entry.uploadStatus === "skipped"
                                ? "#fffbeb"
                                : "#fff",
                          }}
                        >
                          <div style={{ paddingTop: 4, flexShrink: 0 }}>
                            {renderStatusIcon(entry.uploadStatus)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, fontSize: "0.88rem" }}>
                            <div>
                              <strong>Name:</strong> {entry.name}
                            </div>
                            <div>
                              <strong>Card label:</strong> {entry.cardLabel}
                            </div>
                            <div>
                              <strong>Camp:</strong> {entry.camp}
                            </div>
                            <div>
                              <strong>Created at:</strong> {entry.createdAt}
                            </div>
                            <div style={{ wordBreak: "break-word" }}>
                              <strong>Entitlements:</strong> {entry.entitlements}
                            </div>
                            {entry.message && (
                              <div
                                className={
                                  entry.uploadStatus === "success"
                                    ? "text-success"
                                    : entry.uploadStatus === "skipped"
                                    ? "text-warning"
                                    : "text-danger"
                                }
                                style={{ marginTop: 6, whiteSpace: "pre-wrap" }}
                              >
                                {entry.message}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {filteredIndices.length > LIST_PAGE_SIZE && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span className="text-muted small">
                        Showing {pageStart + 1}–
                        {Math.min(pageStart + LIST_PAGE_SIZE, filteredIndices.length)} of{" "}
                        {filteredIndices.length} filtered records
                      </span>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          disabled={safeListPage === 0}
                          onClick={() => setListPage((p) => Math.max(0, p - 1))}
                        >
                          Previous
                        </Button>
                        <span className="align-self-center small text-muted">
                          Page {safeListPage + 1} / {totalListPages}
                        </span>
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          disabled={safeListPage >= totalListPages - 1}
                          onClick={() =>
                            setListPage((p) => Math.min(totalListPages - 1, p + 1))
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {rows.length > 0 && filteredIndices.length === 0 && (
                <Alert color="light" className="mt-3 mb-0">
                  No records match the selected filter.
                </Alert>
              )}

              <div className="mt-4">
                <Button color="secondary" onClick={resetUpload} disabled={isProcessing}>
                  Reset
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UploadCva;
